<script setup lang="ts">
import { Pagination } from '@/shared/ui/Pagination'
import { DEFAULT_PAGINATION_LIMIT } from '@/shared/constants'

/**
 * Rendered as the sibling right after the table, and carries the gap above the pager — so a
 * list widget declares no margin of its own. Props and events line up with what `useEntityTable` returns
 * (`pageModel`, `limitForPagination`, `onPagination`).
 *
 * Nothing but `page` / `limit` / `total` reaches the pager. The root here is a layout
 * `div` with fallthrough left on, so an `auto-scroll`, `layout` or `background` written
 * on this tag lands on that `div` and nothing reports it; render `Pagination` directly
 * when you need one of them.
 */
withDefaults(
  defineProps<{
    total?: number
    page?: number
    limit?: number
  }>(),
  {
    total: 0,
    page: 1,
    limit: DEFAULT_PAGINATION_LIMIT
  }
)

const emit = defineEmits<{
  (e: 'update:page', value: number): void
  (e: 'pagination', payload: { newPage: number; limit: number }): void
}>()
</script>

<template>
  <div class="table-pagination-wrapper">
    <slot />
    <Pagination
      :total="total"
      :page="page"
      :limit="limit"
      @update:page="emit('update:page', $event)"
      @pagination="emit('pagination', $event)"
    />
  </div>
</template>

<style lang="scss" scoped>
.table-pagination-wrapper {
  margin-top: 16px;
}
</style>
